// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { updateBundle } from '@/bundle/api/update-bundle';
import { EditBundleDialog } from '@/bundle/components/edit-bundle-dialog';
import {
  bundleQueryKey,
  bundlesQueryKey,
  useBundle,
} from '@/bundle/hooks/use-bundles';
import { ArchiveControl } from '@/components/archive-control';
import { Link } from '@/components/link';
import { NotFoundErrorBoundary } from '@/components/not-found-error-boundary';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { SupportSnackbar } from '@/components/support-snackbar';
import { VerticalNavigationLink } from '@/components/vertical-navigation-link';
import { EditRounded } from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import {
  Box,
  Breadcrumbs,
  Chip,
  Container,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Outlet, createFileRoute } from '@tanstack/react-router';
import { Suspense } from 'react';

/**
 * Route configuration for the bundle detail page.
 * The route parameter 'bundleKey' identifies the specific bundle.
 */
export const Route = createFileRoute('/_app/catalog/bundles/$bundleKey')({
  component: BundleRoute,
});

function BundleRoute() {
  const { bundleKey } = Route.useParams();

  return (
    <NotFoundErrorBoundary
      entityType="bundle"
      listPath="/catalog/bundles"
      listLabel="Go to bundles"
    >
      <BundleContent bundleKey={bundleKey} />
    </NotFoundErrorBoundary>
  );
}

/**
 * Bundle detail page component.
 *
 * Features:
 * - Displays bundle name, description, and archive status
 * - Allows editing name and description via dialog
 * - Supports archiving/unarchiving the bundle
 * - Vertical sidebar navigation to the Items and Audit log sections
 */
function BundleContent(props: { bundleKey: string }) {
  const queryClient = useQueryClient();

  const { bundle } = useBundle(props.bundleKey);

  const archiveBundleMutation = useMutation({
    mutationFn: (isArchived: boolean) => {
      return updateBundle(bundle.key, { isArchived });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: bundleQueryKey(props.bundleKey),
      });
      queryClient.invalidateQueries({ queryKey: bundlesQueryKey });
    },
  });

  const editDialog = useDisclosure();

  const archiveToggleTooltip = bundle.isArchived
    ? 'Unarchive bundle'
    : 'Archive bundle. Archiving hides this bundle from the bundle picker by default but does not change documents it was applied to.';

  const handleChangeArchived = (isArchived: boolean) => {
    archiveBundleMutation.mutate(isArchived);
  };

  return (
    <Container maxWidth="xl">
      <SupportSnackbar
        isOpen={archiveBundleMutation.isError}
        onClose={archiveBundleMutation.reset}
        message="An error occurred while updating the bundle."
      />
      <EditBundleDialog
        isOpen={editDialog.isOpen}
        bundle={bundle}
        onClose={editDialog.close}
      />
      <Stack marginY={3} spacing={3} paddingX={{ xs: 1, md: 3 }}>
        <Stack>
          <Breadcrumbs separator="›">
            <Link
              to="/catalog/bundles"
              variant="body2"
              color="text.secondary"
              sx={{ display: 'block' }}
            >
              Bundles
            </Link>
            <Typography display="flex" variant="body2" color="text.secondary">
              {bundle.name}
            </Typography>
          </Breadcrumbs>
          <Stack
            useFlexGap
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            marginBottom={1}
          >
            <Stack useFlexGap direction="row" alignItems="center" spacing={1}>
              <Typography variant="h5">{bundle.name}</Typography>
              {bundle.isArchived && <Chip size="small" label="Archived" />}
            </Stack>
            <Stack direction="row" spacing={1}>
              <Tooltip arrow title="Edit bundle">
                <IconButton size="small" onClick={editDialog.open}>
                  <EditRounded fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip arrow placement="top" title={archiveToggleTooltip}>
                <span>
                  <ArchiveControl
                    isArchived={bundle.isArchived}
                    isDisabled={archiveBundleMutation.isPending}
                    onChange={handleChangeArchived}
                  />
                </span>
              </Tooltip>
            </Stack>
          </Stack>
          {bundle.description !== null && (
            <Typography variant="body2" color="text.secondary">
              {bundle.description}
            </Typography>
          )}
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Stack
            direction={{ xs: 'row', md: 'column' }}
            borderTop="1px solid transparent"
          >
            <VerticalNavigationLink
              to="/catalog/bundles/$bundleKey"
              params={{ bundleKey: props.bundleKey }}
              activeOptions={{ exact: true }}
            >
              Items
            </VerticalNavigationLink>
            <VerticalNavigationLink
              to="/catalog/bundles/$bundleKey/audit"
              params={{ bundleKey: props.bundleKey }}
            >
              Audit log
            </VerticalNavigationLink>
          </Stack>
          <Box flexGrow={1} paddingLeft={{ xs: 0, md: 2 }}>
            <SupportErrorBoundary message="An error occurred.">
              <Suspense>
                <Outlet />
              </Suspense>
            </SupportErrorBoundary>
          </Box>
        </Stack>
      </Stack>
    </Container>
  );
}
